import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Table, Popover } from "antd";
import MainLayout from "../../Layout/mainLayout";
import HierarchyChain from "./hierarchyChain";
import { ListPartnersByHierarchy } from "../../Redux/Actions/franchiseManagerAction";
import { getLocalityHeadsList } from "../../Redux/Actions/franchiseManagerAction";
import "./viewFranchiseManger.css";

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return "—";
    }
};

const ListLocalityHeads = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { franchise_id, city_head_id } = useParams();

    const { admin } = useSelector((state) => state.admin || {});
    const accessCode = admin?.data?.access_code || [];
    const canAdd = Array.isArray(accessCode) && accessCode.includes("add");

    const {
        localityHeads = [],
        localityHeadsLoading,
    } = useSelector((state) => state.franchiseManager || {});

    useEffect(() => {
        if (franchise_id && city_head_id) {
            dispatch(getLocalityHeadsList({ franchise_id, city_head: city_head_id }));
        }
    }, [franchise_id, city_head_id, dispatch]);

    // Derive hierarchy names directly from the populated records — no separate fetch needed
    const franchiseManagerName = localityHeads[0]?.franchise_id?.name;
    const cityHeadName = localityHeads[0]?.city_head?.name;

    const PartnersViewButton = ({ to, franchise_id, city_head_id, locality_head_id }) => {
        const dispatch = useDispatch();
        const [hasPartners, setHasPartners] = useState(null); // null = checking

        useEffect(() => {
            let mounted = true;
            const payload = { franchise_id };
            if (city_head_id) payload.city_head_id = city_head_id;
            if (locality_head_id) payload.locality_head_id = locality_head_id;

            dispatch(ListPartnersByHierarchy(payload))
                .then((res) => {
                    if (!mounted) return;
                    const count = res?.data?.length ?? res?.length ?? 0;
                    setHasPartners(count > 0);
                })
                .catch(() => mounted && setHasPartners(false));
            return () => { mounted = false; };
        }, [franchise_id, city_head_id, locality_head_id, dispatch]);

        if (hasPartners === null) {
            return (
                <span
                    className="vfm-btn vfm-btn--primary"
                    style={{ padding: "6px 14px", fontSize: 12.5, color: "white", opacity: 0.5 }}
                >
                    View
                </span>
            );
        }

        return hasPartners ? (
            <Link to={to} className="vfm-btn vfm-btn--primary" style={{ padding: "6px 14px", fontSize: 12.5, color: "white", whiteSpace: "nowrap" }}>
                View
            </Link>
        ) : (
            <span
                className="vfm-btn vfm-btn--primary"
                style={{ padding: "6px 14px", fontSize: 12.5, color: "white", opacity: 0.4, cursor: "not-allowed", pointerEvents: "none", whiteSpace: "nowrap" }}
            >
                No Partners
            </span>
        );
    };

    const columns = useMemo(() => [
        {
            title: "Sr No.",
            align: "center",
            render: (_, __, index) => index + 1,
        },
        {
            title: "Name",
            align: "center",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Email",
            align: "center",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "Phone",
            align: "center",
            key: "contact_number",
            render: (_, record) =>
                record.contact_number ? `${record.country_code || ""} ${record.contact_number}` : "—",
        },
        {
            title: "Cities",
            align: "center",
            key: "city",
            render: (_, record) => {
                const cities = record.city || [];
                return (
                    <Popover
                        title="Cities"
                        trigger="click"
                        content={
                            <div style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 200, maxHeight: 240, overflowY: "auto" }}>
                                {cities.length > 0
                                    ? cities.map((c) => <span key={c._id}>{c.city_name}</span>)
                                    : <span>None</span>}
                            </div>
                        }
                    >
                        <span
                            style={{
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 26,
                                height: 26,
                                padding: "0 8px",
                                borderRadius: 999,
                                background: "#eff6ff",
                                color: "#2563eb",
                                fontSize: 12.5,
                                fontWeight: 600,
                            }}
                        >
                            {cities.length}
                        </span>
                    </Popover>
                );
            },
        },
        {
            title: "Localities",
            align: "center",
            key: "locality",
            render: (_, record) => {
                const localities = record.locality || [];
                return (
                    <Popover
                        title="Localities"
                        trigger="click"
                        content={
                            <div style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 220, maxHeight: 240, overflowY: "auto" }}>
                                {localities.length > 0
                                    ? localities.map((l) => <span key={l._id}>{l.name}</span>)
                                    : <span>None</span>}
                            </div>
                        }
                    >
                        <span
                            style={{
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 26,
                                height: 26,
                                padding: "0 8px",
                                borderRadius: 999,
                                background: "#f5f3ff",
                                color: "#7c3aed",
                                fontSize: 12.5,
                                fontWeight: 600,
                            }}
                        >
                            {localities.length}
                        </span>
                    </Popover>
                );
            },
        },
        {
            title: "Projects",
            align: "center",
            key: "project",
            render: (_, record) => {
                const projects = record.project || [];
                return (
                    <Popover
                        title="Projects"
                        trigger="click"
                        content={
                            <div style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 220, maxHeight: 240, overflowY: "auto" }}>
                                {projects.length > 0
                                    ? projects.map((p) => <span key={p._id}>{p.project_name}</span>)
                                    : <span>None</span>}
                            </div>
                        }
                    >
                        <span
                            style={{
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 26,
                                height: 26,
                                padding: "0 8px",
                                borderRadius: 999,
                                background: "#fffbeb",
                                color: "#b45309",
                                fontSize: 12.5,
                                fontWeight: 600,
                            }}
                        >
                            {projects.length}
                        </span>
                    </Popover>
                );
            },
        },
        {
            title: "Status",
            align: "center",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <span
                    className={`vfm-status-badge vfm-status-badge--${status === "active" || status === "unblock" ? "active" : "inactive"
                        }`}
                >
                    <span className="vfm-status-dot" />
                    {status || "—"}
                </span>
            ),
        },
        {
            title: "Joined On",
            align: "center",
            dataIndex: "created_at",
            key: "created_at",
            render: (date) => formatDate(date),
        },
        {
            title: "Partners",
            align: "center",
            key: "partners",
            render: (_, record) => (
                <PartnersViewButton
                    to={`/franchise/${franchise_id}/city-heads/${city_head_id}/locality-heads/${record._id}/partners`}
                    franchise_id={franchise_id}
                    city_head_id={city_head_id}
                    locality_head_id={record._id}
                />
            ),
        },
        {
            title: "Details",
            align: "center",
            key: "view",
            render: (_, record) => (
                <Link
                    to={`/franchise/${franchise_id}/city-heads/${city_head_id}/locality-heads/${record._id}/view`}
                    className="vfm-btn vfm-btn--primary"
                    style={{ padding: "6px 14px", fontSize: 12.5, color: "white" }}
                >
                    View
                </Link>
            ),
        },
    ], [franchise_id, city_head_id]);

    return (
        <MainLayout>
            <div className="vfm-wrapper">
                <div className="vfm-header">
                    <div className="vfm-header-left">
                        <button
                            className="vfm-back-btn"
                            onClick={() => navigate(`/franchise/${franchise_id}/city-heads`)}
                            type="button"
                            aria-label="Go back"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                        </button>
                        <div>
                            <h1 className="vfm-title">Locality Heads</h1>
                            <p className="vfm-subtitle">All locality heads reporting to this city head</p>
                        </div>
                    </div>

                    {canAdd && (
                        <Link
                            to={`/franchise/${franchise_id}/city-heads/${city_head_id}/locality-heads/add`}
                            className="vfm-btn vfm-btn--primary"
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px", fontSize: 13, color: "white" }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            Add Locality Head
                        </Link>
                    )}
                </div>

                <HierarchyChain
                    nodes={[
                        { label: "Franchise Manager", name: franchiseManagerName },
                        { label: "City Head", name: cityHeadName },
                        { label: "Locality Heads", name: "Viewing all", active: true },
                    ]}
                />

                <div className="vfm-section-card">
                    <Table
                        dataSource={Array.isArray(localityHeads) ? localityHeads : []}
                        columns={columns}
                        loading={localityHeadsLoading}
                        rowKey="_id"
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 1100 }}
                    />
                </div>
            </div>
        </MainLayout>
    );
};

export default ListLocalityHeads;